import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';

import { ActivateAccountRequestDTO, GearbeltEvent, GetCurrentWebUserResponseDTO, WorkspaceDTO } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { GearbeltLoader } from '@gearbelt/ui/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { Countries, PhoneCheckContext, TimezoneMapping } from '@site-mate/sitemate-global-shared/lib';
import { useMutation } from '@tanstack/react-query';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { jwtDecode } from 'jwt-decode';
import { HTTPError } from 'ky';
import { User } from 'oidc-client-ts';
import ReCAPTCHA from 'react-google-recaptcha';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useForm } from 'react-hook-form';
import { useAuth } from 'react-oidc-context';
import { useSearchParams } from 'react-router-dom';

import GearbeltLogo from '@/assets/gearbelt.svg';
import CheckIcon from '@/components/icons/check';
import CircleIcon from '@/components/icons/circle';
import CloseSmallIcon from '@/components/icons/close-small';
import { Checkbox } from '@/components/ui/checkbox';
import { Form, FormFieldItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Toaster, useToast } from '@/components/ui/toaster';
import { env } from '@/environment';
import { userManager } from '@/lib/fusionauth';
import { ApiService } from '@/services/api-v2';
import { signupOtpService } from '@/services/global-api/signup-otp.service';
import { workspaceService } from '@/services/global-api/workspace.service';
import { useUserStore } from '@/stores/user.store';
import { setForceLogoutFlagForSSOSignUp } from '@/utils/auth';
import { useEvents } from '@/utils/events';
import { convertImageUrl } from '@/utils/image-url';
import { useWorkspaceNavigate } from '@/utils/routes';

import { SignUpFooter } from '../signup';
import { IPasswordValidation, PasswordValidationsList } from '../signup/password-validations';
import { PhoneVerificationErrorReason, PhoneVerificationStatus, PhoneVerificationStep } from '../signup/sign-up-phone-verification';
import { validatePassword } from '../signup/signup-form-utils';
import { mapSendErrorReason, mapVerifyErrorReason, maskPhone, VALID_CAPTCHA_STATUS } from '../signup/signup-otp-utils';
import { AccountActivationStepProps, ActivateAccountFormData, activateAccountFormSchema, ActivationSteps } from './activate-account-form-utils';

export const ActivateAccountPage = () => (
  <GoogleReCaptchaProvider reCaptchaKey={env.recaptcha.v3SiteKey}>
    <ActivateAccountPageInner />
  </GoogleReCaptchaProvider>
);

const ActivateAccountPageInner = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [activationStep, setActivationStep] = useState<ActivationSteps>(ActivationSteps.UserInfo);
  const [workspaceInfo, setWorkspaceInfo] = useState<{ logoUrl: string | null; name: string }>({ logoUrl: '', name: '' });
  const [termsAndConditionsAccepted, setTermsAndConditionsAccepted] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const navigate = useWorkspaceNavigate();
  const userStore = useUserStore();
  const userIdSearchParam = useMemo(() => searchParams.get('userId'), [searchParams]);
  const codeSearchParam = useMemo(() => searchParams.get('code'), [searchParams]);
  const workspaceIdSearchParam = useMemo(() => searchParams.get('workspaceId'), [searchParams]);
  const auth = useAuth();
  const { trackEvent } = useEvents();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const otpTwoFactorIdRef = useRef<string | null>(null);
  const verificationTokenRef = useRef<string | null>(null);
  const [otpStatus, setOtpStatus] = useState<PhoneVerificationStatus>('entry');
  const [otpErrorReason, setOtpErrorReason] = useState<PhoneVerificationErrorReason | undefined>(undefined);
  const [otpCooldownSeconds, setOtpCooldownSeconds] = useState(0);
  const [useReCAPTCHAV2, setUseReCAPTCHAV2] = useState(false);
  const [reCAPTCHAV2Token, setReCAPTCHAV2Token] = useState<string | null>(null);
  const otpDeliveryMethodRef = useRef<'sms' | 'call'>('sms');
  const otpProfileMatchCountRef = useRef<number>(0);

  useEffect(() => {
    loadEmailAndLogo();
    trackEvent(GearbeltEvent.UserAccountActivatedByEmail);
  }, []);

  useEffect(() => {
    if (auth.isAuthenticated && userStore.org?.workspaceId) {
      navigate('/dashboard');
    }
  }, [auth.isAuthenticated, userStore.org, navigate]);

  async function loadEmailAndLogo() {
    try {
      if (!userIdSearchParam || !codeSearchParam) {
        throw new Error('An error has occurred, please check your details and try again');
      } else if (!workspaceIdSearchParam) {
        throw new Error('This activation link has expired, please ask your admin for a new one');
      }
      await getWorkspaceBranding();
      await verifyActivationId();
    } catch (error) {
      if (error instanceof HTTPError) {
        const parsedError = await error.response.json();
        toast.error(`An error occurred: ${parsedError.message}`);
      } else if (error instanceof Error) {
        toast.error(`An error occurred: ${error.message}`);
      } else {
        toast.error('An error occurred, please check your details and try again');
      }
    } finally {
      setLoading(false);
    }
  }

  async function verifySSODomain(email: string) {
    const domain = email.split('@')[1];
    const { isSSODomain } = await workspaceService.isSSODomain(domain);
    if (isSSODomain) {
      form.setValue('password', '');
    }
    form.setValue('isSSOSignUp', isSSODomain);
  }

  const form = useForm<ActivateAccountFormData>({
    resolver: zodResolver(activateAccountFormSchema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      password: '',
      confirmedPassword: '',
      mobile: '',
      timezone: dayjs.tz.guess(),
      isSSOSignUp: false,
    },
    mode: 'onChange',
  });

  const isSSOSignUp = form.watch('isSSOSignUp');

  async function verifyActivationId() {
    const response = await ApiService.VerifyActivationId({
      activationId: codeSearchParam!,
    });
    const { email } = await response.json();
    if (email) {
      form.setValue('email', email);
      await verifySSODomain(email);
    }
  }

  async function getWorkspaceBranding() {
    const response = await ApiService.GetWorkspaceBranding({ workspaceId: workspaceIdSearchParam });
    const { logoUrl, name } = await response.json();
    setWorkspaceInfo({ logoUrl, name });
  }

  const isNextButtonDisabled = useMemo(() => {
    const { email, firstName, lastName, mobile } = form.getValues();
    const isEmptyField = !email.length || !firstName.length || !lastName.length || !mobile.length;
    const formHasEmptyFields = Object.keys(form.formState.errors).length > 0;
    return isEmptyField || !termsAndConditionsAccepted || formHasEmptyFields;
  }, [form.formState, termsAndConditionsAccepted]);

  const { mutateAsync: activateAccount } = useMutation({
    mutationFn: ({ ...values }: ActivateAccountRequestDTO & {}) => {
      return ApiService.ActivateAccount({
        ...values,
      });
    },

    onSuccess: async (result) => {
      const { loginResult, user } = await result.json();
      const oidcUser = new User({
        token_type: 'Bearer',
        access_token: loginResult.accessToken,
        expires_at: loginResult.accessTokenExpiration,
        refresh_token: loginResult.refreshToken,
        profile: jwtDecode(loginResult.accessToken),
      });

      if (userManager) {
        await userManager.storeUser(oidcUser);
      }

      userManager.events.load(oidcUser);
      userStore.setUser({ ...user, email: form.getValues().email } as unknown as GetCurrentWebUserResponseDTO);
      userStore.setOrg(user.workspaces[0] as unknown as WorkspaceDTO);
      if (isSSOSignUp) {
        setForceLogoutFlagForSSOSignUp();
      }
    },

    onError: async (error: HTTPError) => {
      const errorMessage = await error.response.json<{ message: string }>().then((json) => json.message);
      toast.error(`Account activation error: ${errorMessage}`);
    },
  });

  function proceedAfterUserInfo() {
    if (isSSOSignUp) {
      const placeholderPassword = 'SSOSignup123';
      form.setValue('password', placeholderPassword);
      form.setValue('confirmedPassword', placeholderPassword);
      onSubmit();
    } else {
      setActivationStep(ActivationSteps.Password);
    }
  }

  async function onUserInfoNextStep() {
    trackEvent(GearbeltEvent.CTAClicked, {
      Context: 'Invited Next 1',
    });

    setLoading(true);
    try {
      const phone = form.getValues().mobile;
      const { clash, matchCount } = await ApiService.CheckPhoneOnSignup({ phone, context: PhoneCheckContext.InviteActivation });

      if (!clash) {
        proceedAfterUserInfo();
        return;
      }

      otpProfileMatchCountRef.current = matchCount;
      await sendOtp();
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function sendOtp(deliveryMethod: 'sms' | 'call' = 'sms', useV2Fallback = false) {
    otpDeliveryMethodRef.current = deliveryMethod;
    const phone = form.getValues().mobile;
    let recaptchaToken = '';
    if (useV2Fallback) {
      recaptchaToken = reCAPTCHAV2Token ?? '';
    } else if (executeRecaptcha) {
      recaptchaToken = await executeRecaptcha('send_otp');
    }

    let sendResult: Awaited<ReturnType<typeof signupOtpService.send>>;
    try {
      sendResult = await signupOtpService.send(phone, recaptchaToken, deliveryMethod, useV2Fallback, PhoneCheckContext.InviteActivation);
    } catch (error) {
      const { reason, cooldownSeconds } = mapSendErrorReason(error);
      setOtpErrorReason(reason);
      setOtpCooldownSeconds(cooldownSeconds);
      setOtpStatus('error');
      setActivationStep(ActivationSteps.PhoneVerification);
      return;
    }

    // Low v3 score → server withholds the code and asks for the v2 checkbox challenge.
    if (sendResult.status !== VALID_CAPTCHA_STATUS || !sendResult.twoFactorId) {
      trackEvent(GearbeltEvent.ActivationHumanVerificationRequired, {});
      setUseReCAPTCHAV2(true);
      setActivationStep(ActivationSteps.HumanVerification);
      return;
    }

    otpTwoFactorIdRef.current = sendResult.twoFactorId;
    trackEvent(GearbeltEvent.ActivationOtpSent, { profileMatchCount: otpProfileMatchCountRef.current });
    setUseReCAPTCHAV2(false);
    setReCAPTCHAV2Token(null);
    setOtpErrorReason(undefined);
    setOtpCooldownSeconds(0);
    setOtpStatus('entry');
    setActivationStep(ActivationSteps.PhoneVerification);
  }

  async function onHumanVerificationNext() {
    trackEvent(GearbeltEvent.CTAClicked, { Context: 'Activation Human Verification' });
    setLoading(true);
    try {
      await sendOtp(otpDeliveryMethodRef.current, true);
    } finally {
      setLoading(false);
    }
  }

  async function onOtpVerify(code: string) {
    if (!otpTwoFactorIdRef.current) {
      setOtpErrorReason('expired');
      setOtpStatus('error');
      return;
    }

    setOtpErrorReason(undefined);
    setOtpStatus('submitting');
    try {
      const { verificationToken: token } = await signupOtpService.verify(otpTwoFactorIdRef.current, code);
      verificationTokenRef.current = token;
      trackEvent(GearbeltEvent.ActivationOtpVerified);
    } catch (error) {
      const failureReason = mapVerifyErrorReason(error);
      trackEvent(GearbeltEvent.ActivationOtpFailed, { failureReason });
      setOtpErrorReason(failureReason);
      setOtpStatus('error');
      return;
    }

    proceedAfterUserInfo();
    setOtpStatus('entry');
  }

  async function onSubmit() {
    setLoading(true);
    const { confirmedPassword, ...formValues } = form.getValues();
    const values: ActivateAccountRequestDTO = {
      ...formValues,
      userId: userIdSearchParam as string,
      activationId: codeSearchParam as string,
      workspaceId: workspaceIdSearchParam as string,
      ...(verificationTokenRef.current ? { verificationToken: verificationTokenRef.current } : {}),
    };
    await activateAccount(values);
  }

  return loading ? (
    <GearbeltLoader size={32} className="m-auto" />
  ) : (
    <>
      <div className="bg-grey-1 h-full w-full">
        {activationStep === ActivationSteps.PhoneVerification ? (
          <div className="mt-12 flex justify-center">
            <PhoneVerificationStep
              status={otpStatus}
              errorReason={otpErrorReason}
              serverCooldownSeconds={otpCooldownSeconds}
              maskedPhone={maskPhone(form.getValues().mobile)}
              onVerify={onOtpVerify}
              onResend={() => sendOtp('sms', useReCAPTCHAV2)}
              onRequestCall={() => sendOtp('call', useReCAPTCHAV2)}
              onUseDifferentNumber={() => {
                otpTwoFactorIdRef.current = null;
                verificationTokenRef.current = null;
                setOtpErrorReason(undefined);
                setOtpStatus('entry');
                setActivationStep(ActivationSteps.UserInfo);
              }}
            />
          </div>
        ) : activationStep === ActivationSteps.HumanVerification ? (
          <div className="mt-12 flex justify-center">
            <ActivationHumanVerificationStep loading={loading} onVerified={setReCAPTCHAV2Token} onNextStep={onHumanVerificationNext} />
          </div>
        ) : (
          <div className="z-10 m-auto mt-12 flex min-h-[277px] w-[400px] flex-col items-center gap-4 rounded bg-white px-8 py-6 shadow-lg">
            {workspaceInfo.logoUrl && <img src={convertImageUrl(workspaceInfo.logoUrl)} alt="workspace-logo" className="h-auto max-w-25 text-center" />}
            {activationStep === ActivationSteps.UserInfo && (
              <ActivateAccountUserInfoStep
                form={form}
                workspaceName={workspaceInfo.name}
                onNextStep={onUserInfoNextStep}
                termsAndConditionsAccepted={termsAndConditionsAccepted}
                onTermsAndConditionsChange={() => setTermsAndConditionsAccepted(!termsAndConditionsAccepted)}
                isNextButtonDisabled={isNextButtonDisabled}
              />
            )}
            {activationStep === ActivationSteps.Password && (
              <ActivateAccountPasswordStep
                form={form}
                onNextStep={() => {
                  onSubmit();
                }}
              />
            )}
            <div className="flex items-center justify-center gap-0.5 py-1">
              <a onClick={() => setActivationStep(ActivationSteps.UserInfo)}>
                <CircleIcon
                  className={clsx({
                    'text-default-blue': activationStep === ActivationSteps.UserInfo,
                    'text-grey-4': activationStep !== ActivationSteps.UserInfo,
                  })}
                  size={10}
                />
              </a>
              {!isSSOSignUp && (
                <a
                  onClick={() => {
                    if (!isNextButtonDisabled) setActivationStep(ActivationSteps.Password);
                  }}>
                  <CircleIcon
                    className={clsx({
                      'text-default-blue': activationStep === ActivationSteps.Password,
                      'text-grey-4': activationStep !== ActivationSteps.Password,
                    })}
                    size={10}
                  />
                </a>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-col items-center justify-center gap-1 pt-8">
          <img src={GearbeltLogo} alt="gearbelt-logo" className="h-8 w-auto" />
          <SignUpFooter className="text-sm" />
        </div>
      </div>
      <Toaster />
    </>
  );
};

const ActivationHumanVerificationStep = ({
  loading,
  onVerified,
  onNextStep,
}: {
  loading: boolean;
  onVerified: (token: string | null) => void;
  onNextStep: () => void;
}) => {
  const [isVerified, setIsVerified] = useState(false);

  return (
    <div className="z-10 m-auto flex min-h-[277px] w-[400px] flex-col gap-4 rounded bg-white p-8 shadow">
      <h2 className="text-xl font-semibold">Are you human?</h2>
      <p className="text-default-text text-sm">Please complete the challenge below.</p>
      <ReCAPTCHA
        sitekey={env.recaptcha.v2SiteKey}
        size="normal"
        onChange={(token) => {
          setIsVerified(Boolean(token));
          onVerified(token);
        }}
        onExpired={() => {
          setIsVerified(false);
          onVerified(null);
        }}
      />
      <Button size="lg" className="w-full py-2" type="button" loading={loading} onClick={onNextStep} disabled={!isVerified}>
        Finish
      </Button>
    </div>
  );
};

const ActivateAccountUserInfoStep = ({
  form,
  workspaceName,
  onNextStep,
  onTermsAndConditionsChange,
  termsAndConditionsAccepted,
  isNextButtonDisabled,
}: AccountActivationStepProps) => {
  const defaultCountry = 'au';
  const defaultContinent = 'OC';
  const userCountry = TimezoneMapping[dayjs.tz.guess()]?.countries[0]?.code?.toLowerCase() ?? defaultCountry;
  const continentByTimezone = Countries.find((c) => c.ccode.toLowerCase() === userCountry)?.continentCode || defaultContinent;
  const [selectedCountryContinent, setSelectedCountryContinent] = useState<string>(continentByTimezone);

  const europeContinentCode = 'EU';
  const urlMap = {
    [europeContinentCode]: {
      privacyPolicy: 'https://sitemate.com/uk/resources/privacy-policy',
      termsOfService: 'https://sitemate.com/uk/resources/terms-of-service',
    },
    default: {
      privacyPolicy: 'https://sitemate.com/resources/privacy-policy',
      termsOfService: 'https://sitemate.com/resources/terms-of-service',
    },
  };

  const handleSelectedCountry = (selectedCountry: string) => {
    const continent = Countries.find((c) => c.ccode.toLowerCase() === selectedCountry)?.continentCode;
    setSelectedCountryContinent(continent || 'OC');
  };

  return (
    <>
      <header>
        <h2 className="pb-2 text-center text-xl font-medium">Activate your Gearbelt Account</h2>
        <p className="text-default-text text-center text-sm">
          Once your account is created, you will gain internal access to the
          <span className="pl-0.5 font-medium">{workspaceName}</span> workspace.
        </p>
      </header>
      <Form {...form}>
        <form className="space-between flex flex-col gap-2">
          {form.getValues().email && (
            <FormFieldItem control={form.control} name="email">
              {() => <Input defaultValue={form.getValues().email} {...form.register('email')} disabled={true} className="bg-grey-2 cursor-not-allowed" />}
            </FormFieldItem>
          )}
          <FormFieldItem control={form.control} name="firstName">
            {({ value, error }) => (
              <Input placeholder="First name" value={value as string} error={!!error} {...form.register('firstName')} disabled={!form.getValues().email} />
            )}
          </FormFieldItem>

          <FormFieldItem control={form.control} name="lastName">
            {({ value, error }) => (
              <Input placeholder="Last name" value={value as string} error={!!error} {...form.register('lastName')} disabled={!form.getValues().email} />
            )}
          </FormFieldItem>
          <FormFieldItem control={form.control} name="mobile">
            {({ value, onChange, error }) => {
              const { onChange: registerOnChange } = form.register('mobile');
              return (
                <PhoneInput
                  placeholder="Phone number..."
                  value={value as string}
                  error={!!error}
                  onChange={(value) => {
                    onChange(value);
                    registerOnChange({ target: { value } });
                  }}
                  onCountryChange={handleSelectedCountry}
                  userCountry={userCountry}
                  disabled={!form.getValues().email}
                />
              );
            }}
          </FormFieldItem>
          <label className="flex items-center gap-1 pt-1 text-sm">
            <Checkbox checked={termsAndConditionsAccepted} onCheckedChange={onTermsAndConditionsChange} />
            <span>
              I agree to the
              <a
                className="text-default-blue px-1 underline"
                href={selectedCountryContinent === 'EU' ? urlMap[selectedCountryContinent].privacyPolicy : urlMap.default.privacyPolicy}
                target="_blank"
                rel="noopener">
                Privacy Policy
              </a>
              &
              <a
                className="text-default-blue pl-1 underline"
                href={selectedCountryContinent === 'EU' ? urlMap[selectedCountryContinent].termsOfService : urlMap.default.termsOfService}
                target="_blank"
                rel="noopener">
                Terms of Service
              </a>
            </span>
          </label>
          <Button size="lg" className="mt-3 w-full text-base" type="button" loading={false} onClick={onNextStep} disabled={isNextButtonDisabled}>
            {form.getValues().isSSOSignUp ? 'Activate your account' : 'Next'}
          </Button>
        </form>
      </Form>
    </>
  );
};

const ActivateAccountPasswordStep = ({ form, onNextStep }: AccountActivationStepProps) => {
  const {
    control,
    watch,
    formState: { errors },
  } = form;
  const password = watch('password');
  const confirmedPassword = watch('confirmedPassword');
  const [passwordValidation, setPasswordValidation] = useState<IPasswordValidation | null>(null);

  function onPasswordChange(event: ChangeEvent<HTMLInputElement>): void {
    if (!event.target.value) {
      setPasswordValidation(null);
      return;
    }
    setPasswordValidation(validatePassword(event.target.value));
  }

  return (
    <>
      <header>
        <h2 className="pb-2 text-center text-xl font-medium">Almost there!</h2>
        <p className="text-default-text text-center text-sm">Create and confirm your Gearbelt account password below. </p>
      </header>

      <Form {...form}>
        <form className="space-between flex w-full flex-col gap-2">
          <FormFieldItem control={form.control} name="password" noErrorMessage={true}>
            {({ value, onChange }) => {
              const { onChange: registerOnChange } = form.register('password');
              return (
                <PasswordInput
                  placeholder="Password"
                  value={value as string}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    onPasswordChange(e);
                    registerOnChange(e);
                    onChange(e.target.value);
                  }}
                />
              );
            }}
          </FormFieldItem>
          {passwordValidation && errors.password && <PasswordValidationsList {...passwordValidation} />}
          <FormFieldItem control={control} name="confirmedPassword" noErrorMessage={true}>
            {({ value, onChange }) => {
              const { onChange: registerOnChange } = form.register('confirmedPassword');
              return (
                <PasswordInput
                  placeholder="Confirm password"
                  value={value as string}
                  error={(value && password !== value) || !!errors.confirmedPassword}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    registerOnChange(e);
                    onChange(e.target.value);
                  }}
                />
              );
            }}
          </FormFieldItem>
          {(errors.confirmedPassword || (confirmedPassword && password !== confirmedPassword)) && (
            <div>
              <div className={clsx('flex items-center text-xs', confirmedPassword.length === 0 ? 'text-default-red' : 'text-default-green')}>
                {confirmedPassword.length === 0 ? <CloseSmallIcon /> : <CheckIcon />}
                <span>Confirm password is required</span>
              </div>
              <div
                className={clsx(
                  'flex items-center text-xs',
                  password.length == 0 || password !== confirmedPassword ? 'text-default-red' : 'text-default-green',
                )}>
                {password.length === 0 || password !== confirmedPassword ? <CloseSmallIcon /> : <CheckIcon />}
                <span>Confirm password matches the password</span>
              </div>
            </div>
          )}
          <Button
            size="lg"
            className="mt-3 w-full py-3 text-base"
            type="button"
            onClick={onNextStep}
            disabled={!form.getValues().password.length || !!form.formState.errors.password || password !== confirmedPassword}>
            Activate your account
          </Button>
        </form>
      </Form>
    </>
  );
};
